import React, {useEffect, useState, useRef} from 'react';
import b_ from 'b_';

import {SurveyPreset} from 'client/common/types';
import {useUserRoles} from 'client/common/hooks/user';
import {Icon} from 'client/common/components/lego';

import {scrollToElement} from 'client/common/utils/scroll';
import {tld} from 'client/common/utils/tld';

import 'client/common/utils/request-idle-callback';

import I18N from 'assets/i18n/brandlift-project';

import {getPresetGroups} from './consts';
import {PresetProps, SurveyPresetsProps} from './types';
import {testIds} from './test-ids';

import './survey-presets.css';

const i18n = I18N.surveyCreate.surveyPresets;
const b = b_.with('survey-presets');

function Preset(props: PresetProps) {
    const {name, onClick, selected} = props;
    const self = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selected && self.current) {
            scrollToElement(self.current);
        }
    }, [selected, self]);

    return (
        <div className={b('preset', {selected})} onClick={onClick} ref={self} data-testid={testIds[name]}>
            <div className={b('preset-info')}>
                <div className={b('preset-name')}>{i18n.presets[name].name}</div>
            </div>
            <div className={b('preset-overlay')} />
            {name === SurveyPreset.Crowd && (
                <div className={b('preset-badge')}>
                    <Icon className={b('preset-icon')} type='magic-white' />
                    {i18n.badges.new}
                </div>
            )}
            {name === SurveyPreset.Simple && (
                <div className={b('preset-badge')}>
                    <Icon className={b('preset-icon')} type='magic-white' />
                    {i18n.badges.yaAudience}
                </div>
            )}
            {name === SurveyPreset.BannerTest && (
                <div className={b('preset-badge')}>
                    <Icon className={b('preset-icon')} type='magic-white' />
                    {i18n.badges.beta}
                </div>
            )}
        </div>
    );
}

// eslint-disable-next-line max-statements
export default function SurveyPresets(props: SurveyPresetsProps) {
    const {onPresetClick, preset} = props;

    const [groupTitle, setGroupTitle] = useState('');

    const userRoles = useUserRoles();

    const filteredPresetGroups = getPresetGroups({
        tld,
        userRoles,
    });

    useEffect(() => {
        if (!groupTitle && preset) {
            const [initialTitle] = Object.entries(filteredPresetGroups)
                .filter(([, group]) => group.includes(preset))
                .map(([title]) => title);

            if (initialTitle) {
                setGroupTitle(initialTitle);
            }
        }
    }, [groupTitle, preset, filteredPresetGroups]);

    const createGroupClickHandler = (title: string) => () => {
        setGroupTitle(title);
    };

    const presetClickHandler = (presetName: SurveyPreset) => () => {
        onPresetClick(presetName);
    };

    return (
        <div className={b()}>
            <section className={b('header')}>
                <h1 className={b('header-title')}>{i18n.title}</h1>
            </section>
            {Object.entries(filteredPresetGroups).map(([title, presetNames]) => (
                <section
                    className={b('group')}
                    key={title}
                    onClick={createGroupClickHandler(title)}
                >
                    <h2 className={b('group-title')}>{title}</h2>
                    <div className={b('group-items')}>
                        {presetNames.map(presetName => (
                            <Preset
                                name={presetName}
                                onClick={presetClickHandler(presetName)}
                                selected={title === groupTitle && presetName === preset}
                                key={presetName}
                            />
                        ))}
                    </div>
                </section>
            ))}
            <div className={b('overlay')} />
        </div>
    );
}
